import React, { useEffect, useMemo, useState } from "react";
import {
  FaCar,
  FaDollarSign,
  FaCheckCircle,
  FaTimesCircle,
  FaRoute,
  FaClock,
  FaChartBar,
  FaCalendar,
  FaDownload,
  FaArrowUp,
  FaArrowDown,
} from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { toast } from "react-toastify";
import SortableTh from "../components/SortableTh";

const getTrendPeriodSortValue = (trend, groupBy) => {
  const id = trend?._id || {};
  const year = Number(id.year) || 0;
  const month = Number(id.month) || 1;
  const day = Number(id.day) || 1;
  const hour = Number(id.hour) || 0;
  if (groupBy === "hour") {
    return new Date(year, month - 1, day, hour).getTime();
  }
  if (groupBy === "day") {
    return new Date(year, month - 1, day).getTime();
  }
  return new Date(year, month - 1, 1).getTime();
};

const formatTrendPeriod = (trend, groupBy) => {
  const id = trend?._id || {};
  if (groupBy === "hour") {
    return `${id.year}-${String(id.month).padStart(2, "0")}-${String(id.day).padStart(
      2,
      "0"
    )} ${String(id.hour).padStart(2, "0")}:00`;
  }
  if (groupBy === "day") {
    return `${id.year}-${String(id.month).padStart(2, "0")}-${String(id.day).padStart(
      2,
      "0"
    )}`;
  }
  return `${id.year}-${String(id.month).padStart(2, "0")}`;
};

const compareTrendRows = (a, b, key, dir, groupBy) => {
  const mult = dir === "desc" ? -1 : 1;
  let va;
  let vb;
  switch (key) {
    case "period":
      va = getTrendPeriodSortValue(a, groupBy);
      vb = getTrendPeriodSortValue(b, groupBy);
      break;
    case "count":
      va = Number(a.count) || 0;
      vb = Number(b.count) || 0;
      break;
    case "completed":
      va = Number(a.completed) || 0;
      vb = Number(b.completed) || 0;
      break;
    case "revenue":
      va = Number(a.revenue) || 0;
      vb = Number(b.revenue) || 0;
      break;
    default:
      return 0;
  }
  if (va < vb) return -1 * mult;
  if (va > vb) return 1 * mult;
  return 0;
};

const RideStatistics = () => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1))
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
    groupBy: "day",
  });
  const [trendSort, setTrendSort] = useState({ key: null, direction: "asc" });

  const trendSortThClass =
    "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100";

  const handleTrendSortSet = (key, direction) => {
    setTrendSort({ key, direction });
  };

  const handleTrendSortLabelClick = (key) => {
    setTrendSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const trendSortTh = (columnKey, label) => (
    <SortableTh
      columnKey={columnKey}
      label={label}
      activeKey={trendSort.key}
      activeDirection={trendSort.direction}
      onSortSet={handleTrendSortSet}
      onSortLabelClick={handleTrendSortLabelClick}
      className={trendSortThClass}
      theme="green"
    />
  );

  const sortedTrends = useMemo(() => {
    const trends = statistics?.trends;
    if (!Array.isArray(trends) || !trends.length) return [];
    if (!trendSort.key) return [...trends];
    return [...trends].sort((a, b) =>
      compareTrendRows(a, b, trendSort.key, trendSort.direction, dateRange.groupBy)
    );
  }, [statistics?.trends, trendSort.key, trendSort.direction, dateRange.groupBy]);

  // Fetch statistics
  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");

      const queryParams = new URLSearchParams({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        groupBy: dateRange.groupBy,
      });

      const response = await axiosInstance.get(
        `${API.ADMIN.RIDES_STATISTICS}?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching statistics:", error);
      toast.error("Failed to fetch statistics");
    } finally {
      setLoading(false);
    }
  };

  // Export statistics
  const handleExport = async () => {
    try {
      const token = localStorage.getItem("adminToken");

      const queryParams = new URLSearchParams({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        format: "json",
      });

      const response = await axiosInstance.get(
        `${API.ADMIN.RIDES_EXPORT}?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Create downloadable file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataUri =
        "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
      const link = document.createElement("a");
      link.setAttribute("href", dataUri);
      link.setAttribute(
        "download",
        `ride-statistics-${dateRange.startDate}-to-${dateRange.endDate}.json`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Statistics exported successfully!");
    } catch (error) {
      console.error("Error exporting statistics:", error);
      toast.error("Failed to export statistics");
    }
  };

  useEffect(() => {
    fetchStatistics();
  }, [dateRange]);

  const formatCurrency = (amount) => {
    const roundedAmount = amount ? parseFloat(amount.toFixed(2)) : 0;
    return `₹${roundedAmount.toLocaleString()}`;
  };

  const formatPercentage = (value) => {
    const roundedValue = value ? parseFloat(value.toFixed(2)) : 0;
    return `${roundedValue}%`;
  };

  const formatDistance = (distance) => {
    const roundedDistance = distance ? parseFloat(distance.toFixed(2)) : 0;
    return `${roundedDistance}km`;
  };

  const getChangeIndicator = (current, previous) => {
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    const isPositive = change > 0;

    return (
      <div
        className={`flex items-center text-sm ${
          isPositive ? "text-green-600" : "text-red-600"
        }`}
      >
        {isPositive ? (
          <FaArrowUp className="mr-1" />
        ) : (
          <FaArrowDown className="mr-1" />
        )}
        {Math.abs(change).toFixed(1)}%
      </div>
    );
  };

  if (loading && !statistics) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading statistics...</span>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Ride Analytics & Statistics
            </h1>
            <p className="text-gray-600">
              Comprehensive insights into ride performance and trends
            </p>
          </div>
          {/* <button
            onClick={handleExport}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <FaDownload className="mr-2" />
            Export Data
          </button> */}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              className="border rounded-lg px-3 py-2 w-full"
              value={dateRange.startDate}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, startDate: e.target.value }))
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              className="border rounded-lg px-3 py-2 w-full"
              value={dateRange.endDate}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, endDate: e.target.value }))
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Group By
            </label>
            <select
              className="border rounded-lg px-3 py-2 w-full"
              value={dateRange.groupBy}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, groupBy: e.target.value }))
              }
            >
              <option value="hour">Hour</option>
              <option value="day">Day</option>
              <option value="month">Month</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchStatistics}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {statistics && (
        <>
          {/* Overall Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FaCar className="text-blue-500 text-2xl mr-3" />
                  <div>
                    <p className="text-gray-500 text-sm">Total Rides</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {statistics.overall?.totalRides || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FaDollarSign className="text-green-500 text-2xl mr-3" />
                  <div>
                    <p className="text-gray-500 text-sm">Total Revenue</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(statistics.overall?.totalRevenue)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FaRoute className="text-purple-500 text-2xl mr-3" />
                  <div>
                    <p className="text-gray-500 text-sm">Total Distance</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {formatDistance(statistics.overall?.totalDistance)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FaCheckCircle className="text-emerald-500 text-2xl mr-3" />
                  <div>
                    <p className="text-gray-500 text-sm">Completion Rate</p>
                    <p className="text-2xl font-bold text-emerald-600">
                      {formatPercentage(statistics.overall?.completionRate)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <FaDollarSign className="text-orange-500 text-2xl mr-3" />
                <div>
                  <p className="text-gray-500 text-sm">Avg Fare</p>
                  <p className="text-xl font-bold text-orange-600">
                    {formatCurrency(statistics.overall?.avgFare)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <FaRoute className="text-teal-500 text-2xl mr-3" />
                <div>
                  <p className="text-gray-500 text-sm">Avg Distance</p>
                  <p className="text-xl font-bold text-teal-600">
                    {formatDistance(statistics.overall?.avgDistance)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <FaDollarSign className="text-indigo-500 text-2xl mr-3" />
                <div>
                  <p className="text-gray-500 text-sm">Total Commission</p>
                  <p className="text-xl font-bold text-indigo-600">
                    {formatCurrency(statistics.overall?.totalCommission)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <FaTimesCircle className="text-red-500 text-2xl mr-3" />
                <div>
                  <p className="text-gray-500 text-sm">Cancellation Rate</p>
                  <p className="text-xl font-bold text-red-600">
                    {formatPercentage(statistics.overall?.cancellationRate)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Status Breakdown */}
          {statistics.statusBreakdown && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FaChartBar className="mr-2 text-blue-500" />
                  Rides by Status
                </h3>
                <div className="space-y-3">
                  {statistics.statusBreakdown.map((status, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <div
                          className={`w-3 h-3 rounded-full mr-3 ${
                            status._id === "Completed"
                              ? "bg-green-500"
                              : status._id === "Cancelled"
                              ? "bg-red-500"
                              : status._id === "Started"
                              ? "bg-blue-500"
                              : status._id === "Accepted"
                              ? "bg-yellow-500"
                              : "bg-gray-500"
                          }`}
                        ></div>
                        <span className="font-medium">
                          {status._id || "Unknown"}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{status.count}</div>
                        <div className="text-sm text-gray-500">
                          {formatCurrency(status.revenue)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FaCar className="mr-2 text-purple-500" />
                  Rides by Category
                </h3>
                <div className="space-y-3">
                  {statistics.categoryBreakdown &&
                    statistics.categoryBreakdown.map((category, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center">
                          <div
                            className={`w-3 h-3 rounded-full mr-3 ${
                              category._id === "In-City"
                                ? "bg-blue-500"
                                : "bg-purple-500"
                            }`}
                          ></div>
                          <span className="font-medium">{category._id}</span>
                        </div>
                        <div className="text-right">
                          <div className="font-bold">{category.count}</div>
                          <div className="text-sm text-gray-500">
                            {formatCurrency(category.revenue)}
                          </div>
                          <div className="text-xs text-gray-400">
                            Avg: {formatDistance(category.avgDistance)}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* Payment Breakdown */}
          {statistics.paymentBreakdown && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FaDollarSign className="mr-2 text-green-500" />
                Payment Method Breakdown
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {statistics.paymentBreakdown.map((payment, index) => (
                  <div
                    key={index}
                    className="text-center p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="font-medium text-gray-900">
                      {payment._id || "Pending"}
                    </div>
                    <div className="text-2xl font-bold text-blue-600 mt-2">
                      {payment.count}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatCurrency(payment.revenue)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trends */}
          {statistics.trends && statistics.trends.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FaCalendar className="mr-2 text-indigo-500" />
                Ride Trends ({dateRange.groupBy}ly)
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {trendSortTh("period", "Period")}
                      {trendSortTh("count", "Total Rides")}
                      {trendSortTh("completed", "Completed")}
                      {trendSortTh("revenue", "Revenue")}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedTrends.slice(0, 10).map((trend, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatTrendPeriod(trend, dateRange.groupBy)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {trend.count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {trend.completed}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(trend.revenue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {!statistics && !loading && (
        <div className="text-center py-12">
          <FaChartBar className="text-gray-400 text-4xl mx-auto mb-4" />
          <p className="text-gray-500">
            No statistics available for the selected date range
          </p>
        </div>
      )}
    </div>
  );
};

export default RideStatistics;
